import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { partial, every, first, indexOf, noop } from 'underscore';
import { Stack } from '@fluentui/react';
import { SmartTextEditor, SmartPhoneNumberEditor } from '@bingads-webui-campaign-react/smart-component-text';
import {
  getComponentById,
  DATA_SOURCE_KEY_MAPPING,
} from '@bingads-webui-campaign-react/smart-component-util';
import { useSiteConfig, useEditorStatusContext } from '@bingads-webui-campaign-react/smart-page-context';

import { LocationWithMap } from './location-with-map';
import { CustomizedToggle } from './customized-toggle';
import { dataProperties } from '../data-config';

const hasOnlyOneTrue = (obj, keyName) => {
  if (!obj[keyName]) {
    return false;
  }

  return every(obj, (val, key) => (key !== keyName && !val) || key === keyName);
};

export const ContactMethods = ({
  i18n,
  componentId,
  activeDataProperty,
  onToggle,
  errors,
  locationInfo,
}) => {
  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const [
    { editingComponentType, editingComponentId } = {},
    dispatchEditorStatus = noop,
  ] = useEditorStatusContext() || [];

  const componentConfig = getComponentById(siteConfig, componentId);
  const {
    showEmail = true,
    showPhone = true,
    showLocation = true,
    showMap,
  } = componentConfig;

  const onPhoneNumberToggle = partial(onToggle, dataProperties.phoneNumber);
  const onEmailToggle = partial(onToggle, dataProperties.emails);
  const onLocationToggle = partial(onToggle, dataProperties.locationInfo);
  const onMapToggle = partial(onToggle, dataProperties.bingMap);
  const toggleStates = { showEmail, showPhone, showLocation };

  const phoneNumberLabelId = 'smart-page-contact-phone-number-label-id';
  const emailAddressLabelId = 'smart-page-contact-email-address-label-id';
  const businessAddressLabelId = 'smart-page-contact-business-address-label-id';

  useEffect(() => {
    const needFocusDataProperties = [
      dataProperties.phoneNumber,
      dataProperties.emails,
      dataProperties.locationInfo,
    ];
    // Clear activeDataProperty in case that InputText is always focused.
    if (indexOf(needFocusDataProperties, activeDataProperty) !== -1) {
      dispatchEditorStatus({
        type: 'EDIT_COMPONENT_TYPE',
        payload: {
          editingComponentType,
          editingComponentId,
          editingComponentProperty: null,
        },
      });
    }
  }, [activeDataProperty, dispatchEditorStatus, editingComponentId, editingComponentType]);

  return (
    <div>
      <Stack tokens={{ childrenGap: 15 }}>
        <div data-editable-property={dataProperties.phoneNumber} >
          <SmartPhoneNumberEditor
            i18n={i18n}
            label={i18n.getString(_TL_('Phone number'))}
            onRenderLabel={({ label }) => (
              <CustomizedToggle
                i18n={i18n}
                onToggle={onPhoneNumberToggle}
                defaultChecked={toggleStates.showPhone}
                disabled={hasOnlyOneTrue(toggleStates, 'showPhone')}
                label={label}
                id={phoneNumberLabelId}
              />
            )}
            dataSourceName={DATA_SOURCE_KEY_MAPPING.PhoneNumber}
            focus={activeDataProperty === dataProperties.phoneNumber}
            errorMessage={errors[dataProperties.phoneNumber]}
            ignoreInternalValidate
            disabled={!toggleStates.showPhone}
            aria-labelledby={phoneNumberLabelId}
          />
        </div>
        <div data-editable-property={dataProperties.emails} >
          <SmartTextEditor
            label={i18n.getString(_TL_('Email address'))}
            onRenderLabel={({ label }) => (
              <CustomizedToggle
                i18n={i18n}
                onToggle={onEmailToggle}
                defaultChecked={toggleStates.showEmail}
                disabled={hasOnlyOneTrue(toggleStates, 'showEmail')}
                label={label}
                id={emailAddressLabelId}
              />
            )}
            dataSourceName={DATA_SOURCE_KEY_MAPPING.Emails}
            serializeValue={value => (value ? [value] : [])}
            deSerializeValue={value => first(value)}
            focus={activeDataProperty === dataProperties.emails}
            errorMessage={errors[dataProperties.emails]}
            disabled={!toggleStates.showEmail}
            maxCharCount={100}
            aria-labelledby={emailAddressLabelId}
          />
        </div>
        <div data-editable-property={dataProperties.locationInfo} >
          <LocationWithMap
            i18n={i18n}
            errorMessage={errors[dataProperties.locationInfo]}
            isToggleDisabled={hasOnlyOneTrue(toggleStates, 'showLocation')}
            showLocation={showLocation}
            showMap={showMap}
            locationInfo={locationInfo}
            businessAddressLabelId={businessAddressLabelId}
            activeDataProperty={activeDataProperty}
            dispatchSiteConfig={dispatchSiteConfig}
            onMapToggle={onMapToggle}
            onLocationToggle={onLocationToggle}
          />
        </div>
      </Stack>
    </div>
  );
};

ContactMethods.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  activeDataProperty: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    [dataProperties.phoneNumber]: PropTypes.string,
    [dataProperties.emails]: PropTypes.string,
    [dataProperties.locationInfo]: PropTypes.string,
  }),
  locationInfo: PropTypes.string,
};

ContactMethods.defaultProps = {
  activeDataProperty: null,
  errors: {},
  locationInfo: '',
};
